import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import {
  ClearFiltersButton,
  FiltersGroup,
  useFilters,
  verticalScrollBar
} from '@elfsight/widget-filters';
import { Badge } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useTranslation } from 'react-i18next';
import { FiltersList, Search, useJobFilters } from '../components';
import type { FilterOptions } from '../types';

type SidePanelFiltersViewProps = {
  filterOptions: FilterOptions;
};

export function SidePanelFiltersView({
  filterOptions
}: SidePanelFiltersViewProps) {
  const { t } = useTranslation();
  const {
    filtersAndSearchColor,
    activeFiltersColor,
    jobBorderRadius,
    searchEnabled
  } = useSettings();
  const { filtersSelectedCount, resetFilters } = useFilters();
  const { activeFilters } = useJobFilters();

  const panelBorderRadius = Math.min(34, jobBorderRadius);
  const searchBorderRadius = Math.max(8, Math.min(18, panelBorderRadius - 16));
  const lineColor = getColorWithAlpha(filtersAndSearchColor, 0.2);

  return (
    <SidePanelFilters
      _borderRadius={panelBorderRadius}
      _borderColor={lineColor}
      _backgroundColor={getInvertedColor(filtersAndSearchColor)}
    >
      <Show when={searchEnabled}>
        <SearchWrapper>
          <Search borderRadius={searchBorderRadius} />
        </SearchWrapper>
      </Show>

      <Show when={activeFilters.length > 0}>
        <SidePanelHeader _borderColor={lineColor}>
          <HeaderLeft>
            <SidePanelTitle _textColor={filtersAndSearchColor}>
              {t('Filters')}
            </SidePanelTitle>

            <Show when={filtersSelectedCount > 0}>
              <Badge
                color={activeFiltersColor}
                size="large"
                borderMode="circular"
              >
                {filtersSelectedCount}
              </Badge>
            </Show>
          </HeaderLeft>

          <Show when={filtersSelectedCount > 0}>
            <StyledClearFiltersButton onClick={resetFilters}>
              {t('Clear Filters')}
            </StyledClearFiltersButton>
          </Show>
        </SidePanelHeader>
      </Show>

      <ScrollableContent _scrollbarColor={filtersAndSearchColor}>
        {activeFilters.map((filter) => (
          <StyledFiltersGroup
            key={filter.id}
            title={filter.label}
            _borderColor={filtersAndSearchColor}
          >
            <FiltersList
              activeFilters={[filter]}
              filterOptions={filterOptions}
            />
          </StyledFiltersGroup>
        ))}
      </ScrollableContent>
    </SidePanelFilters>
  );
}

const SidePanelFilters = styled.div<{
  _borderRadius: number;
  _borderColor: string;
  _backgroundColor: string;
}>`
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  flex-shrink: 0;
  width: 300px;
  max-height: 720px;
  transition: background-color 0.2s;
  overflow: hidden;
  border: 1px solid ${({ _borderColor }) => _borderColor};
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
  background-color: ${({ _backgroundColor }) => _backgroundColor};
`;

const SearchWrapper = styled.div.attrs({
  className: createReadableClass({
    component: 'filters',
    element: 'search-wrapper'
  })
})`
  padding: 16px 20px 4px;
`;

const SidePanelHeader = styled.div.attrs({
  className: createReadableClass({
    component: 'filters',
    element: 'side-panel-header'
  })
})<{
  _borderColor: string;
}>`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 12px 20px;
  border-bottom: 1px solid ${({ _borderColor }) => _borderColor};
`;

const HeaderLeft = styled.div.attrs({
  className: createReadableClass({
    component: 'filters',
    element: 'side-panel-header-left'
  })
})`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SidePanelTitle = styled.h3.attrs({
  className: createReadableClass({
    component: 'filters',
    element: 'side-panel-title'
  })
})<{
  _textColor: string;
}>`
  margin: 0;
  height: 36px;
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  color: ${({ _textColor }) => _textColor};
`;

const ScrollableContent = styled.div.attrs({
  className: createReadableClass({
    component: 'filters',
    element: 'side-panel-scrollable'
  })
})<{
  _scrollbarColor: string;
}>`
  overflow-y: auto;
  ${({ _scrollbarColor }) =>
    verticalScrollBar(getColorWithAlpha(_scrollbarColor, 0.2))}
`;

const StyledFiltersGroup = styled(FiltersGroup)<{
  _borderColor: string;
}>`
  padding: 16px 20px 20px;

  &:not(:last-child) {
    border-bottom: 1px solid
      ${({ _borderColor }) => getColorWithAlpha(_borderColor, 0.2)};
  }
`;

const StyledClearFiltersButton = styled(ClearFiltersButton)`
  padding: 8px 12px !important;
`;
